import {
  can,
  type PermissionAction,
  type PermissionDecision,
  type PermissionResource,
  type RoleSet,
} from '../permissions/index.js';
import type { ActForRole } from '../permissions/types.js';
import { getSetting } from '../settings/index.js';

// APP-148 "The one rule": a Stock Admin does an Executive's or a Sector Lead's work for one named
// person, when that person asks. The Stock Admin never gains the permission in their own right:
// the named person's own `can()` decides (D12), so the rule never drifts from theirs.
export type { ActForRole } from '../permissions/types.js';
export { HOLD_REFUSES } from '../permissions/index.js';

/** What "Not what you asked?" says is wrong (D6): the contracts' `QuestionKind`. */
export type QuestionKindId = 'not_asked' | 'different' | 'other';

/**
 * "Not what you asked?": what's wrong, as the Question board words it. One list for the dialog's
 * choices, Waiting on you, the record and the notices, so they can't drift apart.
 */
export const QUESTION_KIND_WORDS: Readonly<Record<QuestionKindId, string>> = {
  not_asked: 'I didn’t ask for this',
  different: 'I asked for something different',
  other: 'Something else',
};

/**
 * Whose review a held award waits on (D17), by whom the questioned action was done for: its mark
 * on the record, the Stock Admin queue and the audit trail. One list for the web and the server.
 */
export const HELD_FOR_WORDS: Readonly<Record<ActForRole, string>> = {
  executive: 'Held for an Executive’s review',
  sector_lead: 'Held for the Sector Lead’s review',
};

/** An action done for someone is a permission action like any other (one action type). */
export type ActForAction = PermissionAction;

/** The two switches, `actFor.executives` and `actFor.sectorLeads` (D1). */
export interface ActForSwitches {
  readonly executives: boolean;
  readonly sectorLeads: boolean;
}

/** Every setting whose edit roles include Executives, as one entry (D20). */
const EXECUTIVE_SETTINGS = 'setting.edit:*';

/**
 * What each role's work covers (the APP-148 tables). Reading (Ask the data, Executive summaries)
 * isn't acting; a PAA's cash share and recalculating aren't an Executive's today (D19); naming
 * delegates hands someone lasting power, so it stays with the lead (D8).
 */
export const ACT_FOR_ACTIONS: Readonly<Record<ActForRole, readonly ActForAction[]>> = {
  executive: [
    'award.approve_overage',
    'award.approve_exec',
    'award.change_taa_split',
    'award.move_taa_person',
    'award.approve_self_recognition',
    // APP-274 (Tom, 2026-11-03): an entry from a delegate who needs that Executive's approval.
    'award.approve_delegate',
    'spot.decide_funding',
    'award.record_exec_decision',
    // An Executive's own Executive award, withdrawn for them before the Stock Admin team.
    'award.cancel',
    'budget.adjust',
    'budget.move',
    EXECUTIVE_SETTINGS,
  ],
  sector_lead: [
    'award.create:SDB',
    'award.create:PAA',
    'award.create:TAA',
    'award.edit_draft',
    'award.submit',
    'award.approve_delegate',
    'award.cancel',
  ],
};

/**
 * Changes the Stock Admin team can already make, marked as done for an Executive (D20): the
 * Stock Admin's own `can()` must pass too. A marker, never new authority.
 */
const marked = (action: ActForAction): boolean =>
  action.startsWith('setting.edit:') || action === 'budget.adjust' || action === 'budget.move';

/**
 * Recording an Executive Discretionary decision made outside the app keeps its authority (the
 * APP-148 table: "Unchanged authority"): the requester or a Stock Admin records it, naming the
 * Executive who decided. So the Stock Admin's own `can()` decides, and the named Executive must
 * be on today's list; the Executive's own right to record isn't the question.
 */
const ownAuthority = (action: ActForAction): boolean => action === 'award.record_exec_decision';

/** A Sector Lead's own entry: the draft they edit and send, or withdraw (D9, D12). */
const leadEntry = (action: ActForAction): boolean =>
  action === 'award.edit_draft' || action === 'award.submit' || action === 'award.cancel';

/** The named person holds the role for this resource today. */
export function holdsRoleFor(
  named: RoleSet,
  role: ActForRole,
  resource: PermissionResource,
): boolean {
  return role === 'executive'
    ? named.roles.includes('executive')
    : resource.sectorId != null && named.leadSectorIds.includes(resource.sectorId);
}

function covers(role: ActForRole, action: ActForAction): boolean {
  const actions = ACT_FOR_ACTIONS[role];
  if (action.startsWith('setting.edit:'))
    return (
      actions.includes(EXECUTIVE_SETTINGS) &&
      getSetting(action.slice('setting.edit:'.length))?.edit.includes('executive') === true
    );
  return actions.includes(action);
}

/**
 * The resource as the named person's own `can()` reads it. A Sector Lead's entry is the lead's
 * when the lead entered it, or a Stock Admin entered it for them; a delegate's entry stays the
 * delegate's to fix (null). The states each action allows are `can()`'s to check.
 */
function resourceFor(
  named: RoleSet,
  role: ActForRole,
  action: ActForAction,
  resource: PermissionResource,
): PermissionResource | null {
  // APP-274: a delegate's entry is decided for whoever it waits on: never the Executive's as the
  // Sector Lead's, or a sector delegate's as an Executive's who happens to lead that sector.
  if (
    action === 'award.approve_delegate' &&
    (role === 'executive') !== (resource.enteredForExecutive === true)
  )
    return null;
  if (role !== 'sector_lead' || !leadEntry(action)) return resource;
  const theirs =
    resource.creatorId === named.personId ||
    (resource.enteredForLead === true && resource.onBehalfOfId === named.personId);
  return theirs ? { ...resource, creatorId: named.personId } : null;
}

const denied = (code: string): PermissionDecision => ({ allowed: false, code });

/**
 * Whether `actor` may take `action` on `resource` for `named`, in `role` (D12). Refusals, in
 * order: `preview_read_only`, `act_for_not_stock_admin`, `act_for_off`, `act_for_not_this`,
 * `act_for_not_in_role`, then the named person's own (`held`, `not_authorized`, …). There are no
 * identity rules (D11): being on the award, or having entered it, refuses nobody.
 */
export function canActFor(
  actor: RoleSet,
  named: RoleSet,
  role: ActForRole,
  action: ActForAction,
  resource: PermissionResource,
  switches: ActForSwitches,
): PermissionDecision {
  if (actor.previewing) return denied('preview_read_only');
  if (!actor.roles.includes('stock_admin')) return denied('act_for_not_stock_admin');
  if (!(role === 'executive' ? switches.executives : switches.sectorLeads))
    return denied('act_for_off');
  if (!covers(role, action)) return denied('act_for_not_this');
  if (!holdsRoleFor(named, role, resource)) return denied('act_for_not_in_role');
  if (ownAuthority(action)) return can(actor, action, resource);
  const theirs = resourceFor(named, role, action, resource);
  if (theirs === null) return denied('act_for_not_this');
  const decision = can(named, action, theirs);
  return decision.allowed && marked(action) ? can(actor, action, resource) : decision;
}
